import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { ArrowRightIcon, EyeIcon } from '@heroicons/react/24/outline';
import cn from 'classnames';

import Button from 'components/form/Button.component';
import LINK from 'constants/link.const';
import useTip from 'hooks/useTip.hook';
import { useTranslation } from 'providers/GILT.provider';
import { useCurrentSalonId } from 'providers/SalonBasicInfo.provider';
import { useTheme } from 'providers/Theme.provider';
import findById from 'utils/arrays/findById.util';
import linkTo from 'utils/links/linkTo.util';

import { createConsultationForm, feConsultationFormUpdate } from '../consultationForms.action';
import { FORM_STATUS, FORM_TEMPLATES } from '../consultationForms.const';
import { selectConsultationFormById } from '../consultationForms.selectors';
import { ExitWithoutSavingModal } from '../modals/ExitWithoutSavingModal.component';
import { ReviewPage } from '../review/ReviewPage.component';
import { SetRulesPage } from '../rules/SetRulesPage.component';

import { FormBuilderBreadcrumb } from './FormBuilderBreadcrumb.component';
import { FormBuilderStepper } from './FormBuilderStepper.component';
import { QuestionsList } from './QuestionsList.component';

import CN from './FormBuilderPage.module.scss';

export const FormBuilderPage = () => {
  const { t } = useTranslation();
  const { formId } = useParams();
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const salonId = useCurrentSalonId();
  const { isDarkMode } = useTheme();
  const existingForm = useSelector((state) => selectConsultationFormById(state, formId));

  const { val: isExitModalOpen, yay: openExitModal, nay: closeExitModal } = useTip(false);
  const stepperRef = useRef(null);
  const [isStepperVisible, setIsStepperVisible] = useState(true);

  useEffect(() => {
    const el = stepperRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(([entry]) => setIsStepperVisible(entry.isIntersecting), { rootMargin: '-50px 0px 0px 0px', threshold: 0 });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const currentStep = Number(new URLSearchParams(location.search).get('step')) || 1;
  const onBack = useCallback(() => (currentStep === 1 ? openExitModal() : history.goBack()), [currentStep, openExitModal, history]);
  const templateId = useMemo(() => new URLSearchParams(location.search).get('template'), [location.search]);
  const template = useMemo(() => findById(templateId, FORM_TEMPLATES), [templateId]);

  const templateSeed = useMemo(() => {
    if (!template || template.id === 'scratch') return null;
    return {
      name: t(template.nameKey),
      questions: template.questions.map((question, index) => ({
        id: `q_new_${Date.now()}_${index}`,
        text: question.text,
        type: question.type,
        required: question.required,
        options: question.options || [],
      })),
    };
  }, [template, t]);

  const defaultValues = useMemo(
    () => ({
      name: existingForm?.name ?? templateSeed?.name ?? '',
      questions: existingForm?.questions ?? templateSeed?.questions ?? [],
    }),
    [existingForm, templateSeed],
  );

  const methods = useForm({ defaultValues });
  const { register, handleSubmit, watch, setValue } = methods;

  // useForm only applies defaultValues on first mount; `questions` are not registered fields.
  useEffect(() => {
    if (existingForm) return;
    if (!templateSeed?.questions?.length) return;

    setValue('name', templateSeed.name, { shouldDirty: false });
    setValue('questions', templateSeed.questions, { shouldDirty: false });
  }, [existingForm, setValue, templateSeed]);

  const [questions, formName] = watch(['questions', 'name']);
  const noQuestions = questions?.length === 0;

  const formsListPath = linkTo({ pattern: LINK.settingsForms, params: { salonId } });

  const handlePersist = (data, status) => {
    if (formId) {
      dispatch(feConsultationFormUpdate({ id: formId, name: data.name, questions: data.questions, status }));
      return formId;
    }
    const newForm = dispatch(createConsultationForm({ name: data.name, questions: data.questions, status }));
    return newForm.id;
  };

  const handleSaveAsDraft = handleSubmit((data) => {
    handlePersist(data, FORM_STATUS.DRAFT);
    history.push(formsListPath);
  });

  const handleSaveAndSetRules = handleSubmit((data) => {
    const id = handlePersist(data, FORM_STATUS.DRAFT);
    const builderPath = linkTo({ pattern: LINK.settingsFormBuilder, params: { salonId, formId: id } });
    history.push(`${builderPath}?step=2`);
  });

  const handleContinueToExit = () => {
    closeExitModal();
    history.push(formsListPath);
  };

  return (
    <div data-bem="FormBuilderPage" className={cn(CN.component, { [CN.darkMode]: isDarkMode })}>
      <FormBuilderBreadcrumb onRequestClose={openExitModal} currentStep={currentStep} onBack={onBack} isStepperVisible={isStepperVisible} />

      <div className={CN.content}>
        <FormBuilderStepper ref={stepperRef} currentStep={currentStep} />

        {currentStep === 3 ? (
          <ReviewPage />
        ) : currentStep === 2 ? (
          <SetRulesPage />
        ) : (
          <FormProvider {...methods}>
            <div className={CN.formSection}>
              <label className={CN.fieldLabel} htmlFor="consultation-form-builder-name">
                {t('consultationForms.builder.formName')}
              </label>
              <input
                id="consultation-form-builder-name"
                data-testid="FormBuilderPage__nameInput"
                className={CN.nameInput}
                type="text"
                placeholder={t('consultationForms.builder.formNamePlaceholder')}
                {...register('name')}
              />
            </div>

            <div className={CN.questionsHeader}>
              <h3 className={CN.questionsTitle}>{t('consultationForms.builder.questions', { count: questions?.length ?? 0 })}</h3>
              <Button variant="empty" data-testid="FormBuilderPage__previewButton" disabled={noQuestions} className={CN.previewButton} width="auto" height="auto" noMargin noVerticalMargin noPadding>
                <EyeIcon className={CN.eyeIcon} />
                <span className={CN.previewButtonText}>{t('consultationForms.builder.preview')}</span>
              </Button>
            </div>

            <QuestionsList />

            <div className={CN.footer}>
              <Button
                variant="outlineForce"
                onClick={handleSaveAsDraft}
                tag="FormBuilderPage__saveDraft"
                data-testid="FormBuilderPage__saveDraft"
                aria-label={t('consultationForms.builder.saveDraft')}
                className={CN.saveDraftButton}
                xSpace
                noMargin
                noVerticalMargin
              >
                {t('consultationForms.builder.saveDraft')}
              </Button>
              <Button
                variant="primaryForce"
                onClick={handleSaveAndSetRules}
                tag="FormBuilderPage__saveRules"
                data-testid="FormBuilderPage__saveRules"
                className={CN.saveRulesButton}
                aria-label={t('consultationForms.builder.saveAndSetRules')}
                disabled={noQuestions || !formName}
                xSpace
                noMargin
                noVerticalMargin
              >
                <span className={CN.saveAndSetRulesButtonContent}>
                  <span>{t('consultationForms.builder.saveAndSetRules')}</span>
                  <ArrowRightIcon className={CN.arrowRightIcon} />
                </span>
              </Button>
            </div>
          </FormProvider>
        )}
      </div>

      <ExitWithoutSavingModal isOpen={isExitModalOpen} onClose={closeExitModal} onSaveAsDraft={handleSaveAsDraft} onContinueToExit={handleContinueToExit} formName={formName} />
    </div>
  );
};
